import {
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  Clock,
  CreditCard,
  Home,
  Info,
  KeyRound,
  Layers,
  LayoutGrid,
  MapPin,
  Maximize2,
  Meh,
  Tag,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import type {
  Measure,
  MinMax,
  PaymentPlan,
  Requirement,
  StructuredExtractedFields,
  ValueSource,
  Years,
} from "@/modules/zoom-calls/types";
import {
  formatLabel,
  isHiddenTechnicalKey,
} from "@/modules/zoom-calls/components/analysis.helpers";

/* ------------------------------------------------------------------ */
/* Primitives                                                          */
/* ------------------------------------------------------------------ */

export const unwrapValueSources = (arr?: ValueSource[] | null): string[] =>
  Array.isArray(arr)
    ? arr
        .filter(Boolean)
        .map((x) => x?.value)
        .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    : [];

/**
 * Normalize a `Measure` field into a `MinMax`. Numeric-ish fields arrive either as a
 * bare scalar (`budget: 15000000`) or a `{min,max}` object — the formatters below all
 * operate on `MinMax`, so scalars are wrapped as `{min}`.
 */
const asMinMax = (v?: Measure | null): MinMax | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number" || typeof v === "string") {
    return String(v).trim() === "" ? null : { min: v };
  }
  if (typeof v === "object") return v;
  return null;
};

/**
 * Reduce an untyped list entry to a primitive: unwrap a `{value, source}` wrapper,
 * keep strings/numbers, drop anything else (never stringify an object).
 */
const unwrapPrimitive = (v: unknown): string | number | null => {
  const raw =
    v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    "value" in (v as Record<string, unknown>)
      ? (v as Record<string, unknown>).value
      : v;
  return typeof raw === "string" || typeof raw === "number" ? raw : null;
};

const parseNumeric = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return isFinite(v) ? v : null;
  const cleaned = v.replace(/,/g, "").replace(/[^\d.]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return isFinite(n) ? n : null;
};

const abbreviateNumber = (n: number): string => {
  if (Math.abs(n) >= 1_000_000) {
    const m = n / 1_000_000;
    return `${Number.isInteger(m) ? m : Number(m.toFixed(1))}M`;
  }
  if (Math.abs(n) >= 1_000) {
    const k = n / 1_000;
    return `${Number.isInteger(k) ? k : Number(k.toFixed(1))}K`;
  }
  return String(n);
};

const withCommas = (v: string | number): string => {
  const n = parseNumeric(v);
  return n !== null ? n.toLocaleString("en-US") : String(v);
};

/* ------------------------------------------------------------------ */
/* Money / area                                                        */
/* ------------------------------------------------------------------ */

/** `{min,max}` or scalar → "13M" / "15M–27M"; single value with `approxSingle` → "~12M". */
export const formatMoneyShort = (
  input?: Measure | null,
  opts?: { approxSingle?: boolean },
): string | null => {
  const v = asMinMax(input);
  if (!v) return null;
  const min = parseNumeric(v.min);
  const max = parseNumeric(v.max);
  if (min === null && max === null) {
    const raw = unwrapPrimitive(v.min ?? v.max);
    return raw !== null && String(raw).trim() !== ""
      ? String(raw).trim()
      : null;
  }
  if (min !== null && max !== null && min !== max) {
    return `${abbreviateNumber(min)}–${abbreviateNumber(max)}`;
  }
  const short = abbreviateNumber((min ?? max) as number);
  return opts?.approxSingle ? `~${short}` : short;
};

export const formatArea = (input?: Measure | null): string | null => {
  const v = asMinMax(input);
  if (!v) return null;
  const min = parseNumeric(v.min);
  const max = parseNumeric(v.max);
  if (min === null && max === null) return null;
  if (min !== null && max !== null && min !== max) return `${min}–${max} m²`;
  return `${min ?? max} m²`;
};

/* ------------------------------------------------------------------ */
/* Unit label (bedrooms + unit type)                                   */
/* ------------------------------------------------------------------ */

const UNIT_TYPE_ABBR: Record<string, string> = {
  apartment: "apt",
  villa: "villa",
  townhouse: "TH",
  twinhouse: "twin",
  duplex: "duplex",
  studio: "studio",
  chalet: "chalet",
  penthouse: "PH",
};

const abbreviateUnitType = (t?: string | null): string | null =>
  t && t.trim()
    ? (UNIT_TYPE_ABBR[t.trim().toLowerCase()] ?? formatLabel(t))
    : null;

const formatBedrooms = (
  bedrooms?: string[] | string | number | null,
): string | null => {
  const arr = Array.isArray(bedrooms)
    ? bedrooms
    : bedrooms != null
      ? [bedrooms]
      : [];
  const vals = arr
    .map(unwrapPrimitive)
    .filter((b): b is string | number => b !== null)
    .map((b) => String(b).trim())
    .filter(Boolean);
  if (vals.length === 0) return null;
  const nums = vals.map((v) => Number(v)).filter((n) => isFinite(n));
  if (nums.length === vals.length && nums.length > 1) {
    return `${Math.min(...nums)}-${Math.max(...nums)}br`;
  }
  return `${vals[0]}br`;
};

export const formatUnitLabel = (
  bedrooms?: string[] | string | number | null,
  unitType?: string | null,
): string | null => {
  const label = [formatBedrooms(bedrooms), abbreviateUnitType(unitType)]
    .filter(Boolean)
    .join(" ");
  return label || null;
};

/* ------------------------------------------------------------------ */
/* Payment plan                                                        */
/* ------------------------------------------------------------------ */

/** A slot (scalar or `{min,max}`) that may hold a percentage ("5%") or an amount ("1,000,000"). */
const formatMoneyOrPercent = (input?: Measure | null): string | null => {
  const v = asMinMax(input);
  if (!v) return null;
  const raw = unwrapPrimitive(v.min ?? v.max);
  if (raw === null) return null;
  const s = String(raw).trim();
  if (s === "") return null;
  return s.endsWith("%") ? s : formatMoneyShort(v);
};

export const formatDownPayment = (pp?: PaymentPlan | null): string | null => {
  const dp = formatMoneyOrPercent(pp?.down_payment);
  return dp ? `${dp} DP` : null;
};

/** First installment-years value; accepts a scalar (`7`), string, or list (`["7"]`). */
const firstYear = (years?: Years | null): string | null => {
  const arr = Array.isArray(years)
    ? years
    : years !== null && years !== undefined
      ? [years]
      : [];
  const first = unwrapPrimitive(arr[0]);
  const y = first !== null ? String(first).trim() : "";
  return y || null;
};

export const formatInstallmentYears = (years?: Years | null): string | null => {
  const y = firstYear(years);
  return y ? `${y} years` : null;
};

/** Pitch "Payment plan" row: "5% DP, 7 years". */
export const formatPaymentPlan = (pp?: PaymentPlan | null): string | null => {
  const parts = [
    formatDownPayment(pp),
    formatInstallmentYears(pp?.installment_years),
  ].filter(Boolean) as string[];
  return parts.length ? parts.join(", ") : null;
};

export const formatMonthlyInstallment = (
  input?: Measure | null,
): string | null => {
  const v = asMinMax(input);
  if (!v) return null;
  const raw = unwrapPrimitive(v.min ?? v.max);
  if (raw === null || String(raw).trim() === "") return null;
  return `${withCommas(raw)} ${v.currency ?? "EGP"}`;
};

export const formatDelivery = (arr?: string[] | null): string | null => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const first = unwrapPrimitive(arr[0]);
  const d = first !== null ? String(first).trim() : "";
  if (!d) return null;
  return d.toLowerCase() === "immediate" ? "Immediate" : d;
};

export const joinList = (arr?: string[] | null, max = 2): string | null => {
  if (!Array.isArray(arr)) return null;
  const vals = arr
    .map(unwrapPrimitive)
    .filter((a): a is string | number => a !== null)
    .map((a) => String(a).trim())
    .filter(Boolean);
  if (vals.length === 0) return null;
  const shown = vals.slice(0, max).map((v) => formatLabel(v));
  const extra = vals.length - shown.length;
  return extra > 0 ? `${shown.join(", ")} +${extra}` : shown.join(", ");
};

/* ------------------------------------------------------------------ */
/* Generic value formatter (unknown/passthrough fields)                */
/* ------------------------------------------------------------------ */

const isMinMaxLike = (o: Record<string, unknown>): boolean =>
  "min" in o || "max" in o;

const isValueSourceLike = (o: Record<string, unknown>): boolean => "value" in o;

/**
 * Best-effort human-readable rendering of ANY extracted-field value, so a field is
 * shown whenever the backend carries it — no field is silently dropped. Handles
 * scalars, `{min,max}` ranges, `{value,source}` wrappers, arrays, and nested objects;
 * returns `null` only for genuinely empty values (so the caller can skip the row).
 */
export const formatAnyValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    return isFinite(value) ? value.toLocaleString("en-US") : null;
  }
  if (typeof value === "string") {
    const s = value.trim();
    return s === "" ? null : formatLabel(s);
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => formatAnyValue(v))
      .filter((v): v is string => !!v);
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if (isValueSourceLike(o)) return formatAnyValue(o.value);
    if (isMinMaxLike(o)) return formatMoneyOrPercent(o as MinMax);
    const pairs = Object.entries(o)
      .filter(([k]) => !isHiddenTechnicalKey(k))
      .map(([k, v]) => {
        const fv = formatAnyValue(v);
        return fv ? `${formatLabel(k)}: ${fv}` : null;
      })
      .filter((x): x is string => !!x);
    return pairs.length ? pairs.join(", ") : null;
  }
  return null;
};

/* ------------------------------------------------------------------ */
/* Notes card                                                          */
/* ------------------------------------------------------------------ */

// `requirements` has its own dedicated view; `client_notes` renders as bullet
// lines rather than a grid row — both are excluded from the generic Notes grid.
export const NOTES_EXCLUDED_KEYS = new Set(["requirements", "client_notes"]);

/**
 * Whether the Notes card would render anything for this payload. Lets callers
 * (e.g. ComprehensiveAnalysis) drop the section entirely when it is empty.
 */
export const notesHasContent = (
  data: StructuredExtractedFields | null,
): boolean => {
  if (!data) return false;
  const hasRows = Object.entries(data).some(
    ([key, raw]) =>
      !NOTES_EXCLUDED_KEYS.has(key) &&
      !isHiddenTechnicalKey(key) &&
      Boolean(formatAnyValue(raw)),
  );
  const hasNotes =
    Array.isArray(data.client_notes) &&
    data.client_notes.some((n) => String(n).trim());
  return hasRows || hasNotes;
};

/* ------------------------------------------------------------------ */
/* Requirement chips                                                   */
/* ------------------------------------------------------------------ */

export interface Chip {
  Icon: LucideIcon;
  label: string;
}

/** Requirement-level payment chip: "DP 1M, 16K inst/7 years". */
const buildPaymentLabel = (pay: {
  downPayment?: Measure | null;
  monthlyInstallment?: Measure | null;
  installmentYears?: Years | null;
}): string | null => {
  const parts: string[] = [];
  const dp = formatMoneyOrPercent(pay.downPayment);
  if (dp) parts.push(`DP ${dp}`);

  const inst = formatMoneyOrPercent(pay.monthlyInstallment);
  const years = firstYear(pay.installmentYears);
  if (inst && years) parts.push(`${inst} inst/${years} years`);
  else if (inst) parts.push(`${inst} inst`);
  else if (years) parts.push(`${years} years`);

  return parts.length ? parts.join(", ") : null;
};

export const buildRequirementChips = (r: Requirement): Chip[] => {
  const chips: Chip[] = [];
  const push = (Icon: LucideIcon, label: string | null | undefined) => {
    if (label && label.trim()) chips.push({ Icon, label: label.trim() });
  };

  // Each chip renders only from the requirement's own fields — nothing is derived,
  // aggregated, or defaulted; a chip is shown only when the response carries its value.
  // String-shaped slots go through formatAnyValue: fields may arrive as bare strings,
  // numbers, {value, source} wrappers, or arrays of those — never render "[object Object]".
  push(Check, formatAnyValue(r.client_objective ?? r.buying_intent));

  const unitTypes = unwrapValueSources(r.unit_type);
  push(Home, formatUnitLabel(r.bedrooms, unitTypes[0] ?? null));

  push(Maximize2, formatArea(r.unit_area));
  const floorLabel = formatAnyValue(r.floor);
  push(Layers, floorLabel ? `Floor ${floorLabel}` : null);
  push(LayoutGrid, formatAnyValue(r.finishing_type));
  push(MapPin, unwrapValueSources(r.location).join(", ") || null);
  push(Tag, formatMoneyShort(r.budget, { approxSingle: true }));
  push(
    CreditCard,
    buildPaymentLabel({
      downPayment: r.down_payment,
      monthlyInstallment: r.monthly_installment,
      installmentYears: r.installment_years,
    }),
  );
  push(CalendarClock, formatDelivery(r.delivery));
  push(KeyRound, joinList(r.amenities));
  push(Building2, formatAnyValue(r.sale_type));
  push(Clock, formatAnyValue(r.timeline_to_buy));

  return chips;
};

/* ------------------------------------------------------------------ */
/* Client reaction                                                     */
/* ------------------------------------------------------------------ */

export type ReactionColorToken = "red" | "blue" | "green" | "grey";

export interface ReactionStyle {
  Icon: LucideIcon;
  colorToken: ReactionColorToken;
  label: string;
}

export const reactionStyle = (reaction?: string | null): ReactionStyle => {
  switch ((reaction ?? "").toLowerCase()) {
    case "rejected":
    case "negative":
      return { Icon: XCircle, colorToken: "red", label: "Negative" };
    case "requested_info":
      return { Icon: Info, colorToken: "blue", label: "Client request" };
    case "accepted":
    case "positive":
      return { Icon: CheckCircle2, colorToken: "green", label: "Positive" };
    case "neutral":
    default:
      return { Icon: Meh, colorToken: "grey", label: "Neutral" };
  }
};
